"use client";

/**
 * A shop's Games page (/games): its games in three groups, Open now / Coming
 * up / Results (Games v2). The brand comes from the route, like every
 * storefront page. Each game has its own page at /games/<id>.
 */
import Link from "next/link";
import { useParams } from "next/navigation";
import { useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import type { Id } from "@/convex/_generated/dataModel";
import GamesList, { GamesListSkeleton } from "@/components/games/v2/GamesList";
import ShopHeader from "../components/ShopHeader";

export default function ShopGamesPage() {
  const params = useParams<{ domain: string }>();
  const subdomain = (params?.domain ?? "").toLowerCase();
  const brand = useQuery(api.brands.core.getBrandBySubdomain, subdomain ? { subdomain } : "skip") as
    | { _id: Id<"brands">; name?: string; profileImage?: string; customSiteKey?: string }
    | null
    | undefined;

  const loading = !!subdomain && brand === undefined;
  const missing = !subdomain || brand === null;
  const shopHref = brand?.customSiteKey ? "/shop" : "/";
  const name = brand?.name ?? "";
  const initials =
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase())
      .join("") || "•";

  return (
    <div className="min-h-screen bg-white" style={{ color: "#0f172a" }}>
      <ShopHeader brandName={name} />
      <main className="mx-auto w-full max-w-3xl px-4 pb-16 pt-20">
        <Link href={shopHref} className="inline-flex min-h-11 items-center gap-1.5 text-sm font-medium" style={{ color: "#475569" }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0f172a" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M15 18l-6-6 6-6" />
          </svg>
          Shop
        </Link>

        {missing ? (
          <p className="py-16 text-center text-sm" style={{ color: "#64748b" }}>
            This shop isn&apos;t available.
          </p>
        ) : (
          <>
            <div className="mb-5 mt-2 flex items-center gap-3">
              {loading ? (
                <>
                  <div className="h-11 w-11 animate-pulse rounded-full" style={{ background: "#e2e8f0" }} />
                  <div className="flex flex-col gap-2">
                    <div className="h-4 w-40 animate-pulse rounded" style={{ background: "#e2e8f0" }} />
                    <div className="h-3 w-16 animate-pulse rounded" style={{ background: "#e2e8f0" }} />
                  </div>
                </>
              ) : (
                <>
                  {brand?.profileImage ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img src={brand.profileImage} alt="" className="h-11 w-11 shrink-0 rounded-full object-cover" style={{ border: "1px solid #e2e8f0" }} />
                  ) : (
                    <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full text-sm font-extrabold" style={{ background: "#0f172a", color: "#ffffff" }}>
                      {initials}
                    </span>
                  )}
                  <div className="min-w-0">
                    <h1 className="truncate text-lg font-extrabold leading-tight" style={{ color: "#0f172a" }}>
                      {name}
                    </h1>
                    <p className="text-sm" style={{ color: "#64748b" }}>
                      Games
                    </p>
                  </div>
                </>
              )}
            </div>
            {brand?._id ? <GamesList brandId={brand._id} /> : <GamesListSkeleton />}
          </>
        )}
      </main>
    </div>
  );
}
